/** Hero banner with event visual, title, date/location, and register CTA. */
import { useEffect, useState } from 'react';
import { cn } from '@rocco/utils/cn';
import { useHeroEntrance } from './hooks/use-hero-entrance';
import { HDEDesktopFrame } from './components/HDEDesktopFrame';
import { HeroScrollIndicator } from './components/HeroScrollIndicator';
import { HDEPresentedByMark } from './components/HDEPresentedByMark';

const HdeLettersSvg = ({
    className,
    style,
}: {
    className?: string;
    style?: React.CSSProperties;
}) => (
    <svg
        className={className}
        style={style}
        xmlns="http://www.w3.org/2000/svg"
        width="628"
        height="816"
        viewBox="0 0 628 816"
        fill="none"
    >
        <path
            d="M368.318 420.14L368.314 420.154C370.474 420.154 371.978 422.284 371.23 424.288L335.245 520.943C334.867 521.95 333.896 522.621 332.809 522.622H331.512C330.11 522.622 328.964 521.523 328.921 520.137C328.459 505.053 321.61 426.331 228.709 426.331H168.727C155.913 426.331 143.482 431.425 134.945 440.872C126.567 450.147 120.102 464.938 120.102 488.49V595.71H177.672C257.487 595.71 268.923 540.846 270.147 533.024C270.229 532.487 270.695 532.098 271.243 532.098H272.884C273.529 532.098 274.039 532.635 273.995 533.273L264.637 666.438C264.598 667.018 264.113 667.464 263.526 667.464H259.944C259.347 667.464 258.856 666.994 258.832 666.404C258.482 658.279 253.298 606.136 178.148 606.136H120.102V724.28C120.102 741.065 120.029 754.426 120.81 765.078C120.834 765.26 120.854 765.438 120.878 765.62C123.577 786.875 141.002 803.367 162.592 805.371C178.978 806.892 201.689 806.753 233.151 806.753C341.822 806.753 359.242 713.057 360.83 702.742C360.912 702.21 361.378 701.822 361.921 701.821H363.751C364.406 701.821 364.916 702.372 364.853 703.015L354.602 810.422C354.344 813.098 352.068 815.146 349.345 815.146H7.56685C6.67876 815.145 5.9607 814.436 5.96059 813.558V810.499C5.96059 809.602 6.69807 808.882 7.60075 808.911C15.8424 809.156 52.2897 807.161 52.2898 752.633V488.48C52.2898 426.711 7.83431 425.214 0.90288 425.358C0.40788 425.368 0.000139002 424.979 0 424.49V421.008C0 420.529 0.393103 420.14 0.878436 420.14H368.318Z"
            fill="#F4F4F4"
        />
        <path
            fill-rule="evenodd"
            clip-rule="evenodd"
            d="M171.513 0C171.998 0 172.391 0.388408 172.391 0.867943V4.34984C172.391 4.83886 171.984 5.22717 171.489 5.21779C164.558 5.07871 120.102 6.57024 120.102 68.34V189.175H294.59V68.34C294.59 6.57024 250.134 5.07391 243.203 5.21779C242.708 5.2273 242.3 4.83894 242.3 4.34984V0.867943C242.3 0.388513 242.693 0.00017079 243.178 0H405.552C510 0 627.219 55.799 627.219 196.752C627.219 337.705 537.584 395.005 401.349 395.005H249.872C248.984 395.005 248.265 394.296 248.265 393.418V390.358C248.265 389.462 249.003 388.742 249.906 388.771C258.148 389.02 294.594 387.02 294.594 332.492V197.501H120.107V332.492C120.107 387.02 156.553 389.015 164.795 388.771C165.698 388.747 166.436 389.466 166.436 390.358V393.418C166.436 394.296 165.717 395.005 164.829 395.005H7.56685C6.67869 395.005 5.96059 394.296 5.96059 393.418V390.358C5.96059 389.462 6.69807 388.742 7.60075 388.771C15.8425 389.02 52.2898 387.02 52.2898 332.492V68.34C52.2897 6.57047 7.83432 5.07393 0.90288 5.21779C0.407816 5.22738 3.25909e-05 4.83899 0 4.34984V0.867943C5.22803e-05 0.388439 0.393135 5.16093e-05 0.878436 0H171.513ZM373.323 7.28012C369.377 7.28032 366.174 10.4408 366.174 14.3444V190.312C366.174 290.662 366.372 353.175 372.133 364.696C377.812 376.057 379.749 386.228 403.696 386.228C480.321 386.227 558.528 346.029 558.528 197.73C558.528 49.4303 455.832 7.2802 417.915 7.28012H373.323Z"
            fill="#F4F4F4"
        />
    </svg>
);

const TitleSvg = ({
    className,
    style,
}: {
    className?: string;
    style?: React.CSSProperties;
}) => (
    <svg
        className={className}
        style={style}
        viewBox="0 0 638 42"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
    >
        <path
            d="M0 41.098V0.789262H8.74996V16.5745H25.7068V0.789262H34.4567V41.098H25.7068V23.3396H8.74996V41.098H0Z"
            fill="white"
        />
        <path
            d="M58.2122 42C45.4795 42 37.3933 33.0926 37.3933 21.0282C37.3933 8.96376 45.4795 0 58.2122 0C70.9449 0 78.9707 8.96376 78.9707 21.0282C78.9707 33.0926 70.9449 42 58.2122 42ZM58.2725 35.2913C66.1777 35.2913 70.0397 29.0336 70.0397 21.0282C70.0397 12.9664 66.1777 6.70873 58.2725 6.70873C50.3674 6.70873 46.3243 12.9664 46.3243 21.0282C46.3243 29.0336 50.3674 35.2913 58.2725 35.2913Z"
            fill="white"
        />
        <path
            d="M81.946 41.098V0.789262H94.2563L100.351 21.0282C101.86 26.102 103.067 31.9087 103.067 31.9087H103.187C103.187 31.9087 104.334 26.102 105.842 21.0282L111.937 0.789262H124.429V41.098H116.282V17.9839C116.282 14.7141 116.644 9.64027 116.644 9.64027H116.523C116.523 9.64027 115.558 14.3758 114.653 17.3074L107.23 41.098H98.8424L91.6011 17.3074C90.6959 14.3758 89.7304 9.64027 89.7304 9.64027H89.6097C89.6097 9.64027 89.9718 14.7141 89.9718 17.9839V41.098H81.946Z"
            fill="white"
        />
        <path
            d="M129.283 41.098V0.789262H161.025V7.66711H138.033V16.6872H158.188V23.396H138.033V34.2765H161.206V41.098H129.283Z"
            fill="white"
        />
        <path
            d="M173.16 41.098V0.789262H189.997C201.522 0.789262 208.945 9.47114 208.945 21.3664C208.945 27.906 206.591 33.6564 202.066 37.2081C198.867 39.6886 194.703 41.098 189.272 41.098H173.16ZM181.91 34.051H188.669C196.876 34.051 200.074 29.3718 200.074 21.3664C200.074 13.3611 196.333 7.77987 188.91 7.77987H181.91V34.051Z"
            fill="white"
        />
        <path
            d="M211.894 41.098V0.789262H243.635V7.66711H220.644V16.6872H240.799V23.396H220.644V34.2765H243.816V41.098H211.894Z"
            fill="white"
        />
        <path
            d="M263.866 42C252.159 42 245.522 37.1517 245.22 28.6389H253.789C254.392 33.4872 257.349 35.404 263.504 35.404C267.97 35.404 271.892 33.9383 271.892 30.2174C271.892 26.2711 267.789 25.4819 261.211 24.0725C253.487 22.4376 246.427 20.5208 246.427 12.1208C246.427 4.17181 253.366 0.0563751 262.961 0.0563751C272.677 0.0563751 278.952 4.51007 279.616 12.6282H271.228C270.746 8.56913 267.366 6.5396 262.901 6.5396C258.194 6.5396 255.116 8.4 255.116 11.2752C255.116 14.545 258.134 15.5034 264.53 16.8C273.401 18.604 280.642 20.5772 280.642 29.2591C280.642 37.4336 273.582 42 263.866 42Z"
            fill="white"
        />
        <path
            d="M284.118 41.098V0.789262H292.868V41.098H284.118Z"
            fill="white"
        />
        <path
            d="M329.367 41.098L329.065 35.6859H328.944C325.987 40.3087 321.944 41.9436 316.815 41.9436C310.298 41.9436 305.229 39.745 301.668 36.0805C298.108 32.3597 296.056 27.1732 296.056 21.0846C296.056 14.3758 298.591 8.51275 303.117 4.62282C306.617 1.6349 311.203 0 316.996 0C327.858 0 334.073 5.58121 335.461 13.4738H326.892C325.927 9.58389 322.729 6.7651 317.177 6.7651C309.03 6.7651 304.987 13.0792 304.987 21.0846C304.987 29.3154 309.634 35.3477 317.056 35.3477C323.755 35.3477 328.16 30.6685 328.16 26.2148V25.9893H317.78V19.6752H335.944V41.098H329.367Z"
            fill="white"
        />
        <path
            d="M340.251 41.098V0.789262H349.122L363.484 23.2832C364.872 25.4819 366.501 28.9208 366.501 28.9208H366.622C366.622 28.9208 366.38 24.749 366.38 21.9302V0.789262H375.01V41.098H366.561L351.837 18.8295C350.449 16.6872 348.76 13.2483 348.76 13.2483H348.639C348.639 13.2483 348.88 17.4765 348.88 20.2953V41.098H340.251Z"
            fill="white"
        />
        <path
            d="M388.136 41.098V0.789262H419.877V7.66711H396.885V16.6872H417.041V23.396H396.885V34.2765H420.058V41.098H388.136Z"
            fill="white"
        />
        <path
            d="M435.032 41.098L420.489 0.789262H429.661L437.264 24.1289C438.109 26.7785 439.135 30.5557 439.135 30.5557H439.256C439.256 30.5557 440.282 26.7221 441.127 24.1289L448.67 0.789262H457.54L442.997 41.098H435.032Z"
            fill="white"
        />
        <path
            d="M459.179 41.098V0.789262H490.921V7.66711H467.929V16.6872H488.084V23.396H467.929V34.2765H491.102V41.098H459.179Z"
            fill="white"
        />
        <path
            d="M494.731 41.098V0.789262H503.601L517.963 23.2832C519.351 25.4819 520.981 28.9208 520.981 28.9208H521.101C521.101 28.9208 520.86 24.749 520.86 21.9302V0.789262H529.489V41.098H521.041L506.317 18.8295C504.929 16.6872 503.239 13.2483 503.239 13.2483H503.119C503.119 13.2483 503.36 17.4765 503.36 20.2953V41.098H494.731Z"
            fill="white"
        />
        <path d="M534.35 41.098V0.789262H543.1V41.098H534.35Z" fill="white" />
        <path
            d="M547.978 41.098V0.789262H556.848L571.21 23.2832C572.598 25.4819 574.228 28.9208 574.228 28.9208H574.348C574.348 28.9208 574.107 24.749 574.107 21.9302V0.789262H582.736V41.098H574.288L559.564 18.8295C558.176 16.6872 556.486 13.2483 556.486 13.2483H556.366C556.366 13.2483 556.607 17.4765 556.607 20.2953V41.098H547.978Z"
            fill="white"
        />
        <path
            d="M619.157 41.098L618.855 35.6859H618.735C615.778 40.3087 611.735 41.9436 606.605 41.9436C600.088 41.9436 595.019 39.745 591.459 36.0805C587.899 32.3597 585.847 27.1732 585.847 21.0846C585.847 14.3758 588.381 8.51275 592.907 4.62282C596.407 1.6349 600.993 0 606.786 0C617.648 0 623.864 5.58121 625.252 13.4738H616.683C615.717 9.58389 612.519 6.7651 606.967 6.7651C598.821 6.7651 594.778 13.0792 594.778 21.0846C594.778 29.3154 599.424 35.3477 606.847 35.3477C613.545 35.3477 617.95 30.6685 617.95 26.2148V25.9893H607.571V19.6752H625.735V41.098H619.157Z"
            fill="white"
        />
        <path d="M629.19 41.098V32.9235H638V41.098H629.19Z" fill="white" />
    </svg>
);

const TitleStackedSvg = ({
    className,
    style,
}: {
    className?: string;
    style?: React.CSSProperties;
}) => (
    <svg
        xmlns="http://www.w3.org/2000/svg"
        width="169"
        height="97"
        viewBox="0 0 169 97"
        fill="none"
        className={className}
        style={style}
    >
        <path
            d="M24.1641 61.6573V34.5043H35.5053C43.2693 34.5043 48.2692 40.3526 48.2692 48.3656C48.2692 52.7708 46.6839 56.6444 43.6352 59.0369C41.4808 60.7078 38.6759 61.6573 35.0175 61.6573H24.1641ZM30.0583 56.9102H34.611C40.1393 56.9102 42.2938 53.7582 42.2938 48.3656C42.2938 42.973 39.7735 39.2134 34.7736 39.2134H30.0583V56.9102Z"
            fill="#E3E3E3"
        />
        <path
            d="M50.256 61.6573V34.5043H71.6376V39.1374H56.1502V45.2136H69.7271V49.7327H56.1502V57.0621H71.7596V61.6573H50.256Z"
            fill="#E3E3E3"
        />
        <path
            d="M85.2657 62.2649C77.3797 62.2649 72.9082 58.9989 72.705 53.2645H78.4772C78.8837 56.5305 80.8756 57.8217 85.0218 57.8217C88.0299 57.8217 90.6721 56.8343 90.6721 54.3279C90.6721 51.6695 87.9079 51.1379 83.4771 50.1885C78.274 49.0872 73.518 47.796 73.518 42.1375C73.518 36.7829 78.1927 34.0106 84.656 34.0106C91.2005 34.0106 95.4281 37.0107 95.8752 42.4793H90.2249C89.8997 39.745 87.6234 38.3779 84.6153 38.3779C81.4446 38.3779 79.3715 39.6311 79.3715 41.5679C79.3715 43.7705 81.404 44.4161 85.7128 45.2895C91.6883 46.5048 96.5663 47.8339 96.5663 53.6823C96.5663 59.1888 91.8103 62.2649 85.2657 62.2649Z"
            fill="#E3E3E3"
        />
        <path
            d="M98.9078 61.6573V34.5043H104.802V61.6573H98.9078Z"
            fill="#E3E3E3"
        />
        <path
            d="M129.388 61.6573L129.185 58.0115H129.104C127.112 61.1256 124.388 62.2269 120.933 62.2269C116.543 62.2269 113.128 60.7458 110.73 58.2774C108.332 55.771 106.95 52.2771 106.95 48.1757C106.95 43.6566 108.657 39.7071 111.706 37.0867C114.063 35.074 117.153 33.9727 121.055 33.9727C128.372 33.9727 132.559 37.7323 133.494 43.049H127.722C127.071 40.4286 124.917 38.5298 121.177 38.5298C115.689 38.5298 112.966 42.7831 112.966 48.1757C112.966 53.7202 116.096 57.7837 121.096 57.7837C125.608 57.7837 128.575 54.6317 128.575 51.6316V51.4797H121.584V47.2263H133.819V61.6573H129.388Z"
            fill="#E3E3E3"
        />
        <path
            d="M136.72 61.6573V34.5043H142.696L152.37 49.6568C153.305 51.1379 154.403 53.4544 154.403 53.4544H154.484C154.484 53.4544 154.322 50.6442 154.322 48.7454V34.5043H160.135V61.6573H154.444L144.525 46.6567C143.59 45.2136 142.452 42.897 142.452 42.897H142.371C142.371 42.897 142.533 45.7453 142.533 47.6441V61.6573H136.72Z"
            fill="#E3E3E3"
        />
        <path
            d="M0 95.628V68.475H21.3817V73.1081H5.8942V79.1843H19.4711V83.7035H5.8942V91.0328H21.5036V95.628H0Z"
            fill="#E3E3E3"
        />
        <path
            d="M31.5904 95.628L21.7939 68.475H27.9726L33.0945 84.1971C33.6636 85.982 34.3546 88.5264 34.3546 88.5264H34.4359C34.4359 88.5264 35.1269 85.944 35.696 84.1971L40.7772 68.475H46.7527L36.9562 95.628H31.5904Z"
            fill="#E3E3E3"
        />
        <path
            d="M47.8569 95.628V68.475H69.2386V73.1081H53.7511V79.1843H67.328V83.7035H53.7511V91.0328H69.3605V95.628H47.8569Z"
            fill="#E3E3E3"
        />
        <path
            d="M71.8052 95.628V68.475H77.7807L87.4553 83.6275C88.3902 85.1086 89.4877 87.4251 89.4877 87.4251H89.569C89.569 87.4251 89.4064 84.6149 89.4064 82.7161V68.475H95.2193V95.628H89.5284L79.6099 80.6274C78.675 79.1843 77.5368 76.8677 77.5368 76.8677H77.4555C77.4555 76.8677 77.6181 79.716 77.6181 81.6148V95.628H71.8052Z"
            fill="#E3E3E3"
        />
        <path
            d="M98.4935 95.628V68.475H104.388V95.628H98.4935Z"
            fill="#E3E3E3"
        />
        <path
            d="M107.674 95.628V68.475H113.649L123.324 83.6275C124.259 85.1086 125.356 87.4251 125.356 87.4251H125.437C125.437 87.4251 125.275 84.6149 125.275 82.7161V68.475H131.088V95.628H125.397L115.478 80.6274C114.543 79.1843 113.405 76.8677 113.405 76.8677H113.324C113.324 76.8677 113.486 79.716 113.486 81.6148V95.628H107.674Z"
            fill="#E3E3E3"
        />
        <path
            d="M155.622 95.628L155.418 91.9822H155.337C153.345 95.0963 150.622 96.1976 147.167 96.1976C142.776 96.1976 139.362 94.7165 136.964 92.2481C134.565 89.7417 133.183 86.2479 133.183 82.1464C133.183 77.6273 134.89 73.6778 137.939 71.0574C140.297 69.0447 143.386 67.9434 147.289 67.9434C154.605 67.9434 158.792 71.703 159.727 77.0197H153.955C153.305 74.3993 151.15 72.5005 147.41 72.5005C141.923 72.5005 139.199 76.7538 139.199 82.1464C139.199 87.6909 142.329 91.7544 147.329 91.7544C151.841 91.7544 154.809 88.6024 154.809 85.6023V85.4504H147.817V81.197H160.052V95.628H155.622Z"
            fill="#E3E3E3"
        />
        <path
            d="M162.38 95.628V90.1214H168.315V95.628H162.38Z"
            fill="#E3E3E3"
        />
        <path
            d="M51.4258 27.6846V0.531666H57.32V11.165H68.7425V0.531666H74.6367V27.6846H68.7425V15.7221H57.32V27.6846H51.4258Z"
            fill="#E3E3E3"
        />
        <path
            d="M90.6389 28.2922C82.0619 28.2922 76.6148 22.292 76.6148 14.1651C76.6148 6.0382 82.0619 0 90.6389 0C99.216 0 104.622 6.0382 104.622 14.1651C104.622 22.292 99.216 28.2922 90.6389 28.2922ZM90.6796 23.7731C96.0047 23.7731 98.6062 19.5577 98.6062 14.1651C98.6062 8.73451 96.0047 4.51916 90.6796 4.51916C85.3545 4.51916 82.631 8.73451 82.631 14.1651C82.631 19.5577 85.3545 23.7731 90.6796 23.7731Z"
            fill="#E3E3E3"
        />
        <path
            d="M106.627 27.6846V0.531666H114.919L119.025 14.1651C120.041 17.5829 120.854 21.4945 120.854 21.4945H120.935C120.935 21.4945 121.708 17.5829 122.724 14.1651L126.829 0.531666H135.244V27.6846H129.756V12.1144C129.756 9.91177 130 6.49392 130 6.49392H129.919C129.919 6.49392 129.268 9.68391 128.659 11.6587L123.659 27.6846H118.008L113.13 11.6587C112.521 9.68391 111.87 6.49392 111.87 6.49392H111.789C111.789 6.49392 112.033 9.91177 112.033 12.1144V27.6846H106.627Z"
            fill="#E3E3E3"
        />
        <path
            d="M138.514 27.6846V0.531666H159.896V5.16475H144.408V11.2409H157.985V15.7601H144.408V23.0895H160.018V27.6846H138.514Z"
            fill="#E3E3E3"
        />
    </svg>
);

const ArrowSvg = ({
    className,
    style,
}: {
    className?: string;
    style?: React.CSSProperties;
}) => (
    <svg
        className={className}
        style={style}
        viewBox="0 0 24 21"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
    >
        <path
            d="M24 9.04537H5.85484L13.2 2.05765L11.0371 0L0 10.5L11.0371 21L13.2 18.9423L5.85484 11.9546H24V9.04537Z"
            fill="white"
        />
    </svg>
);

interface HeroBannerProps {
    videoSrc: string;
    onRegister?: () => void;
    eventDateText?: string;
    eventLocationText?: string;
    showRegisterButton?: boolean;
    isMobile?: boolean;
    /** When false, hides “Presented by” and sponsor mark (e.g. no active register-module event). */
    showPresentedBySponsor?: boolean;
    presentedByLogoUrl?: string | null;
    presentedByProfilePath?: string | null;
    presentedBySponsorName?: string | null;
}

export const HeroBanner = ({
    videoSrc,
    onRegister,
    eventDateText,
    eventLocationText,
    showRegisterButton = true,
    isMobile = false,
    showPresentedBySponsor = false,
    presentedByLogoUrl,
    presentedByProfilePath,
    presentedBySponsorName,
}: HeroBannerProps) => {
    const stage = useHeroEntrance();
    const [isHydrated, setIsHydrated] = useState(false);

    useEffect(() => {
        setIsHydrated(true);
    }, []);

    if (isMobile) {
        return (
            <section
                data-testid="hero-banner"
                className="relative w-full overflow-hidden bg-black"
                style={{ height: '48.5rem' }}
            >
                {videoSrc ? (
                    <video
                        className="absolute inset-0 h-full w-full object-cover"
                        src={videoSrc}
                        preload="auto"
                        autoPlay
                        muted
                        loop
                        playsInline
                    />
                ) : (
                    <div className="absolute inset-0 bg-[#2a2a2a]" />
                )}
                <div className="absolute inset-0 bg-black/20" />
                <div
                    className="absolute inset-x-0 top-0"
                    style={{
                        height: '2.125rem',
                        background:
                            'linear-gradient(180deg, rgba(0,0,0,0.45) 0%, rgba(0,0,0,0) 100%)',
                    }}
                />
                <div
                    className="absolute inset-x-0 bottom-0"
                    style={{
                        height: '2.125rem',
                        background:
                            'linear-gradient(0deg, rgba(0,0,0,0.45) 0%, rgba(0,0,0,0) 100%)',
                    }}
                />
                <div
                    className="absolute inset-x-[0rem] border-t border-dashed border-white/40"
                    style={{ top: '4.5rem' }}
                />
                <div
                    className="absolute inset-x-[0rem] border-t border-dashed border-white/40"
                    style={{ bottom: '1.875rem' }}
                />
                <HdeLettersSvg
                    className="absolute left-0 top-0"
                    style={{
                        width: '15rem',
                        height: 'auto',
                        transform: 'translate(1.4375rem, 6.65rem)',
                        opacity: 0.95,
                    }}
                />
                <div
                    className="absolute rounded-full bg-white/60"
                    style={{
                        width: '0.3125rem',
                        height: '0.3125rem',
                        left: '0.625rem',
                        top: '3.75rem',
                    }}
                />
                <div
                    className="absolute rounded-full bg-white/60"
                    style={{
                        width: '0.3125rem',
                        height: '0.3125rem',
                        right: '0.625rem',
                        top: '3.75rem',
                    }}
                />
                <div
                    className="absolute rounded-full bg-white/60"
                    style={{
                        width: '0.3125rem',
                        height: '0.3125rem',
                        left: '0.625rem',
                        bottom: '1.165rem',
                    }}
                />
                <div
                    className="absolute rounded-full bg-white/60"
                    style={{
                        width: '0.3125rem',
                        height: '0.3125rem',
                        right: '0.625rem',
                        bottom: '1.165rem',
                    }}
                />
                {showPresentedBySponsor ? (
                    <div className="absolute right-[1.35rem] top-[6.85rem] text-center">
                        <p className="font-sans text-[0.403125rem] font-medium uppercase tracking-[0.08em] text-white/80">
                            Presented by
                        </p>
                        <HDEPresentedByMark
                            logoUrl={presentedByLogoUrl}
                            profilePath={presentedByProfilePath}
                            sponsorName={presentedBySponsorName}
                            className="mt-[0.3125rem]"
                            imageClassName="max-h-[2.5rem] w-[5.375rem]"
                            fallbackLogoClassName="h-auto w-[5.375rem] text-white/90"
                        />
                    </div>
                ) : null}
                <div className="absolute right-[1.5rem] top-[25.5rem]">
                    <TitleStackedSvg />
                </div>

                <div className="absolute bottom-[3.25rem] left-0 w-full px-[1.75rem]">
                    <p className="font-display text-[1.9875rem] font-medium uppercase leading-[0.9] tracking-[-0.03em] text-white">
                        {eventDateText}
                    </p>
                    <p className="mt-[0.1875rem] font-ghost text-[1.9875rem] uppercase leading-[0.9] tracking-[-0.03em] text-white">
                        {eventLocationText}
                    </p>
                    <button
                        type="button"
                        onClick={onRegister}
                        className={cn(
                            'mt-[0.75rem] flex h-[3.25rem] w-full items-center justify-center rounded-full bg-white',
                            showRegisterButton
                                ? ''
                                : 'pointer-events-none invisible',
                        )}
                    >
                        <span className="font-sans text-[0.9375rem] font-medium tracking-[-0.02rem] text-[#141414]">
                            Register Now — Free
                        </span>
                    </button>
                    <p className="mt-[0.875rem] text-center font-display text-[0.875rem] font-medium uppercase text-white">
                        FREE ENTRY / LIMITED SPACES
                    </p>
                </div>
            </section>
        );
    }

    return (
        <section
            data-testid="hero-banner"
            className="relative h-[72.125rem] w-full overflow-hidden bg-black"
        >
            {videoSrc ? (
                <video
                    className="absolute inset-0 h-full w-full object-cover"
                    src={videoSrc}
                    preload="auto"
                    autoPlay
                    muted
                    loop
                    playsInline
                />
            ) : (
                <div className="absolute inset-0 bg-[#2a2a2a]" />
            )}
            <div className="absolute inset-0 bg-black/30" />
            <HDEDesktopFrame className="relative h-full">
                {/* Large HDE serif letters — left side */}
                <HdeLettersSvg
                    className={cn(
                        'absolute left-[3rem] top-[9.75rem] aspect-[627.219/815.146] h-[50.9375rem] w-auto transform-gpu transition-all ease-out [transition-duration:900ms]',
                        stage >= 1
                            ? 'translate-y-0 opacity-100'
                            : 'translate-y-10 opacity-0',
                    )}
                    style={{ visibility: isHydrated ? 'visible' : 'hidden' }}
                />

                {showPresentedBySponsor ? (
                    <div
                        className={cn(
                            'absolute right-12 top-[7.75rem] transform-gpu text-center transition-all duration-700 ease-out',
                            stage >= 2
                                ? 'translate-x-0 opacity-100'
                                : 'translate-x-5 opacity-0',
                        )}
                    >
                        <p className="font-sans text-[0.9375rem] font-medium uppercase tracking-[-0.025rem] text-white/80">
                            Presented by
                        </p>
                        <HDEPresentedByMark
                            logoUrl={presentedByLogoUrl}
                            profilePath={presentedByProfilePath}
                            sponsorName={presentedBySponsorName}
                            className="mt-4"
                            imageClassName="max-h-[3.5rem] w-[11.4375rem]"
                            fallbackLogoClassName="h-auto w-[11.4375rem] text-white/90"
                        />
                    </div>
                ) : null}

                {/* Right-side content area — flex column to prevent overlap */}
                <div
                    className="flex flex-col justify-between"
                    style={{
                        position: 'absolute',
                        left: '67.75rem',
                        top: '30.95rem',
                        bottom: '15rem',
                        visibility: isHydrated ? 'visible' : 'hidden',
                    }}
                >
                    {/* Upper content group */}
                    <div>
                        {/* "HOME DESIGN EVENING." SVG title */}
                        <TitleSvg
                            className={cn(
                                'transform-gpu transition-all duration-700 ease-out',
                                stage >= 2
                                    ? 'translate-x-0 opacity-100'
                                    : 'translate-x-5 opacity-0',
                            )}
                            style={{ width: '39.84rem', height: 'auto' }}
                        />

                        {/* Event details, indented to align with right portion */}
                        <div
                            style={{
                                marginLeft: '24rem',
                                marginTop: '0.3125rem',
                            }}
                        >
                            <p
                                className={cn(
                                    'font-display font-medium uppercase text-white transition-all duration-300 ease-out',
                                    stage >= 3
                                        ? 'translate-y-0 opacity-100'
                                        : 'translate-y-3 opacity-0',
                                )}
                                style={{
                                    fontSize: '1.932rem',
                                    letterSpacing: '-0.058rem',
                                    lineHeight: '0.9',
                                }}
                            >
                                {eventLocationText}
                            </p>
                            <p
                                className={cn(
                                    'font-display font-medium uppercase text-white transition-all duration-300 ease-out',
                                    stage >= 3
                                        ? 'translate-y-0 opacity-100'
                                        : 'translate-y-3 opacity-0',
                                )}
                                style={{
                                    fontSize: '1.932rem',
                                    letterSpacing: '-0.058rem',
                                    lineHeight: '0.9',
                                }}
                            >
                                {eventDateText}
                            </p>

                            <div style={{ marginTop: '1.62rem' }}>
                                <button
                                    type="button"
                                    onClick={onRegister}
                                    className={cn(
                                        'group transition-all duration-300 ease-out',
                                        stage >= 4
                                            ? 'translate-y-0 opacity-100'
                                            : 'translate-y-3 opacity-0',
                                        showRegisterButton
                                            ? ''
                                            : 'pointer-events-none invisible',
                                    )}
                                    style={{ textAlign: 'left' }}
                                >
                                    <p
                                        className="font-display font-medium uppercase text-white"
                                        style={{
                                            fontSize: '1.932rem',
                                            letterSpacing: '-0.058rem',
                                            lineHeight: '0.9',
                                        }}
                                    >
                                        ONE NIGHT ONLY
                                    </p>
                                    <div
                                        className="flex items-center"
                                        style={{ gap: '0.48rem' }}
                                    >
                                        <p
                                            className="font-display font-medium uppercase text-white"
                                            style={{
                                                fontSize: '1.932rem',
                                                letterSpacing: '-0.058rem',
                                                lineHeight: '0.9',
                                            }}
                                        >
                                            REGISTER NOW
                                        </p>
                                        <ArrowSvg
                                            className="rotate-180 transition-transform duration-300 ease-out group-hover:translate-x-1 group-focus-visible:translate-x-1"
                                            style={{
                                                width: '1.5rem',
                                                height: '1.32rem',
                                            }}
                                        />
                                    </div>
                                </button>
                            </div>

                            <div
                                className={cn(
                                    'transition-all duration-300 ease-out',
                                    stage >= 4
                                        ? 'translate-y-0 opacity-100'
                                        : 'translate-y-3 opacity-0',
                                )}
                                style={{ marginTop: '1.62rem' }}
                            >
                                <p
                                    className="font-display font-medium uppercase text-white"
                                    style={{
                                        fontSize: '0.876rem',
                                        letterSpacing: '-0.025rem',
                                        lineHeight: '0.9',
                                    }}
                                >
                                    FREE ENTRY
                                </p>
                                <p
                                    className="font-display font-medium uppercase text-white"
                                    style={{
                                        fontSize: '0.876rem',
                                        letterSpacing: '-0.025rem',
                                        lineHeight: '0.9',
                                        marginTop: '0.192rem',
                                    }}
                                >
                                    LIMITED SPACES
                                </p>
                            </div>
                        </div>
                    </div>

                    {/* Floating register pill — bottom of the content area */}
                    <div
                        className="flex items-center"
                        style={{ marginLeft: '12.6rem' }}
                    >
                        <div
                            className={cn(
                                'transition-all duration-500 ease-out',
                                stage >= 5
                                    ? 'translate-y-0 opacity-100'
                                    : 'translate-y-3 opacity-0',
                            )}
                            style={{
                                marginRight: '1.375rem',
                                textAlign: 'right',
                                marginTop: '0.35rem',
                            }}
                        >
                            <p
                                className="font-display font-medium uppercase text-white"
                                style={{
                                    fontSize: '0.876rem',
                                    letterSpacing: '-0.025rem',
                                    lineHeight: '0.9',
                                }}
                            >
                                HOME DESIGN EVENING
                            </p>
                            <p
                                className="font-display font-medium uppercase text-white"
                                style={{
                                    fontSize: '0.876rem',
                                    letterSpacing: '-0.025rem',
                                    lineHeight: '0.9',
                                }}
                            >
                                {eventDateText}
                            </p>
                        </div>
                        <button
                            type="button"
                            onClick={onRegister}
                            className={cn(
                                'flex items-center justify-center rounded-full bg-white transition-all duration-500 ease-out hover:bg-gray-50/70 active:scale-[0.97]',
                                stage >= 5
                                    ? 'scale-100 opacity-100'
                                    : 'scale-95 opacity-0',
                                showRegisterButton
                                    ? ''
                                    : 'pointer-events-none invisible',
                            )}
                            style={{
                                height: '4.068rem',
                                paddingLeft: '2.88rem',
                                paddingRight: '2.88rem',
                                boxShadow: '0px 6px 37px 0px rgba(0,0,0,0.34)',
                            }}
                        >
                            <span
                                className="whitespace-nowrap font-sans font-medium text-black"
                                style={{
                                    fontSize: '1.38rem',
                                    lineHeight: '2.256rem',
                                }}
                            >
                                Register Now – Free
                            </span>
                        </button>
                    </div>
                </div>

                <HeroScrollIndicator />
            </HDEDesktopFrame>
        </section>
    );
};
